"use client";

import { useUserActivity } from "@/hooks/useUserActivity";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { RoundButtonWithToggleIcon } from "@/components/tools/RoundButtonWithToggleIcon";
import { TrashButton } from "@/components/tools/TrashButton";
import type { UserActivity } from "@/types";
import { deleteUserActivity } from "@/utils";
import { buildAuthPayload } from "@/utils/MyEstateHelper";
import { DocumentArrowUpIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { EyeIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

function formatActivityDate(value: string, language: "cs" | "en") {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(language === "cs" ? "cs-CZ" : "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function ActivityRow({
  activity,
  language,
  showPreview,
  previewLabel,
}: {
  activity: UserActivity;
  language: "cs" | "en";
  showPreview: boolean;
  previewLabel: string;
}) {
  const isImport = activity.type === "BANK_STATEMENT_IMPORT";
  const Icon = isImport ? DocumentArrowUpIcon : EnvelopeIcon;
  const title = isImport
    ? (activity.metadata.fileName ?? "—")
    : (activity.metadata.subject ??
      (language === "cs" ? "Připomínka platby" : "Payment reminder"));
  const details = isImport
    ? [
        activity.metadata.format,
        activity.metadata.estateName,
        activity.metadata.transactionCount != null
          ? language === "cs"
            ? `${activity.metadata.transactionCount} transakcí`
            : `${activity.metadata.transactionCount} transactions`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : [activity.metadata.recipient, activity.metadata.estateName]
        .filter(Boolean)
        .join(" · ");
  const previewItems = isImport
    ? [
        {
          label: language === "cs" ? "Soubor" : "File",
          value: activity.metadata.fileName,
        },
        {
          label: language === "cs" ? "Formát" : "Format",
          value: activity.metadata.format,
        },
        {
          label: language === "cs" ? "Nemovitost" : "Property",
          value: activity.metadata.estateName,
        },
        {
          label: language === "cs" ? "Transakce" : "Transactions",
          value: activity.metadata.transactionCount,
        },
        {
          label: language === "cs" ? "Období" : "Periods",
          value: activity.metadata.periods?.join(", "),
        },
      ]
    : [
        {
          label: language === "cs" ? "Komu" : "To",
          value: activity.metadata.recipient,
        },
        {
          label: language === "cs" ? "Předmět" : "Subject",
          value: activity.metadata.subject,
        },
        {
          label: language === "cs" ? "Nemovitost" : "Property",
          value: activity.metadata.estateName,
        },
        {
          label: language === "cs" ? "Transakce" : "Transaction",
          value: activity.metadata.transactionName,
        },
        {
          label: language === "cs" ? "Období" : "Period",
          value: activity.metadata.period,
        },
      ];
  const visiblePreviewItems = previewItems.filter(
    ({ value }) => value !== undefined && value !== null && value !== "",
  );
  const emailHtml = isImport ? null : activity.metadata.htmlBody;

  return (
    <div className="px-5 py-3 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-softiq-secondary-shade-100/80 text-softiq-primary dark:bg-white/10 dark:text-softiq-secondary">
          <Icon aria-hidden="true" className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-softiq-primary dark:text-white">
            {title}
          </p>
          <p className="app-section-meta mt-1 truncate">{details || "—"}</p>
        </div>
        <time className="shrink-0 text-right text-[10px] text-softiq-primary/40 dark:text-white/40">
          {formatActivityDate(activity.createdAt, language)}
        </time>
      </div>

      {showPreview &&
      visiblePreviewItems.length > 0 &&
      (isImport || !emailHtml) ? (
        <div className="mt-3 border-t border-softiq-primary/10 pt-3 dark:border-white/10">
          <p className="app-section-label mb-2">{previewLabel}</p>
          <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
            {visiblePreviewItems.map(({ label, value }) => (
              <div key={label} className="min-w-0">
                <dt className="text-[9px] uppercase tracking-[0.12em] text-softiq-primary/40 dark:text-white/40">
                  {label}
                </dt>
                <dd className="mt-0.5 truncate text-[11px] text-softiq-primary/75 dark:text-white/75">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      {showPreview && emailHtml ? (
        <div className="mt-3 border-t border-softiq-primary/10 pt-3 dark:border-white/10">
          <p className="app-section-label mb-2">{previewLabel}</p>
          <div className="h-96 overflow-hidden rounded-md border border-border/70 bg-white shadow-sm">
            <iframe
              srcDoc={emailHtml}
              className="h-full w-full border-0 bg-white"
              sandbox=""
              loading="lazy"
              title={`${previewLabel}: ${title}`}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function HomeUserActivity() {
  const { userInfoGlobal } = useAppContext();
  const { language } = useLanguage();
  const { activities, isLoading, refresh } = useUserActivity();
  const [showPreview, setShowPreview] = useState(false);
  const copy = {
    cs: {
      eyebrow: "Aktivita účtu",
      description:
        "Přehled odeslaných připomínek a zpracovaných výpisů.",
      lastImport: "Poslední import",
      emails: "Odeslané připomínky",
      imports: "Importované výpisy",
      noEmails: "Zatím nebyla odeslána žádná připomínka.",
      noImports: "Zatím nebyl importován žádný výpis.",
      loading: "Načítám aktivitu…",
      preview: "Náhled",
      showPreview: "Zobrazit náhled",
      hidePreview: "Schovat náhled",
      deleteAll: "Smazat aktivitu",
      deleteAllTitle: "Opravdu chcete smazat aktivitu účtu?",
      deleteAllDescription:
        "Budou odstraněny odeslané připomínky a záznamy o bankovních importech.",
    },
    en: {
      eyebrow: "Account activity",
      description: "Sent reminders and processed statements.",
      lastImport: "Latest import",
      emails: "Sent reminders",
      imports: "Imported statements",
      noEmails: "No reminder has been sent yet.",
      noImports: "No statement has been imported yet.",
      loading: "Loading activity…",
      preview: "Preview",
      showPreview: "Show preview",
      hidePreview: "Hide preview",
      deleteAll: "Delete activity",
      deleteAllTitle: "Do you really want to delete account activity?",
      deleteAllDescription:
        "Sent reminders and bank import records will be removed.",
    },
  }[language];
  const imports = activities.filter(
    (activity) => activity.type === "BANK_STATEMENT_IMPORT",
  );
  const emails = activities.filter(
    (activity) => activity.type === "TRANSACTION_REMINDER_EMAIL",
  );
  const latestImport = imports[0];

  const handleDeleteActivity = async () => {
    if (!userInfoGlobal?.token || !userInfoGlobal.email) return;
    const payload = buildAuthPayload(
      userInfoGlobal.email,
      userInfoGlobal.token,
      {},
    );
    const response = await deleteUserActivity(payload, userInfoGlobal.token);
    if (response.status === 200) {
      refresh();
    }
  };

  return (
    <section className="app-section-card">
      <div className="app-section-divider-bottom flex flex-wrap items-start justify-between gap-3 px-5 py-4 sm:px-6">
        <div>
          <p className="app-section-eyebrow">{copy.eyebrow}</p>
          <p className="app-section-description mt-1">{copy.description}</p>
        </div>
        <div className="ml-auto flex max-w-full items-start gap-3">
          {latestImport ? (
            <div className="max-w-full text-right">
              <p className="app-section-label">{copy.lastImport}</p>
              <p className="mt-1 max-w-72 truncate text-xs font-medium">
                {latestImport.metadata.fileName}
              </p>
              <p className="app-section-meta mt-1">
                {formatActivityDate(latestImport.createdAt, language)}
              </p>
            </div>
          ) : null}
          <RoundButtonWithToggleIcon
            icon={EyeIcon}
            isActive={showPreview}
            onClick={() => setShowPreview((current) => !current)}
            tooltipTextActive={copy.hidePreview}
            tooltipTextInactive={copy.showPreview}
            activeClassName="text-softiq-secondary"
            className="size-9"
            iconClassName="size-4"
          />
          {activities.length > 0 ? (
            <TrashButton
              label={copy.deleteAll}
              confirmTitle={copy.deleteAllTitle}
              confirmDescription={copy.deleteAllDescription}
              onClick={() => void handleDeleteActivity()}
            />
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <p className="app-section-description px-5 py-6 sm:px-6">
          {copy.loading}
        </p>
      ) : (
        <div className="grid lg:grid-cols-2">
          <div className="border-b border-softiq-primary/10 dark:border-white/10 lg:border-b-0 lg:border-r">
            <p className="px-5 py-3 text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40 sm:px-6">
              {copy.emails}
            </p>
            <div className="app-section-divider-top app-section-divide-y">
              {emails.length > 0 ? (
                emails
                  .slice(0, 5)
                  .map((activity) => (
                    <ActivityRow
                      key={activity.id}
                      activity={activity}
                      language={language}
                      showPreview={showPreview}
                      previewLabel={copy.preview}
                    />
                  ))
              ) : (
                <p className="app-section-description px-5 py-5 sm:px-6">
                  {copy.noEmails}
                </p>
              )}
            </div>
          </div>

          <div>
            <p className="px-5 py-3 text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40 sm:px-6">
              {copy.imports}
            </p>
            <div className="app-section-divider-top app-section-divide-y">
              {imports.length > 0 ? (
                imports
                  .slice(0, 5)
                  .map((activity) => (
                    <ActivityRow
                      key={activity.id}
                      activity={activity}
                      language={language}
                      showPreview={showPreview}
                      previewLabel={copy.preview}
                    />
                  ))
              ) : (
                <p className="app-section-description px-5 py-5 sm:px-6">
                  {copy.noImports}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
